import React, { useEffect, useState } from "react";
import DashLoading from "../components/DashLoading";
import { useIstToday } from "../lib/istDate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SareeIcon from "../components/SareeIcon";
import { DashHero, DashCaption, Section, Tile, TileGrid, DashTable, useDashImage, n, rupees } from "../components/TempleDash";
import { Warehouse, PackageOpen, IndianRupee, ShoppingBag, Inbox, Layers, Tag } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const AX = { fontSize: 11, fill: "#64748B" };
const TONE_CYCLE = ["blue", "green", "yellow", "purple", "red", "orange"];

export default function SareeDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { url, temple } = useDashImage("SAREES", "", user);
  const load = () => api.get("/sarees/dashboard").then((r) => { setData(r.data); setError(null); }).catch((e) => setError(e));
  const istDay = useIstToday(); // new day at 00:00 IST: reload today's figures at once
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [istDay]); // eslint-disable-line

  if (!data) return <DashLoading error={error} onRetry={() => { setError(null); load(); }} testid="saree-dash-loading" />;
  const { stats, category_wise, type_wise, quotas } = data;
  const cats = (category_wise || []).slice(0, 5);
  const quotaRows = quotas.map((q) => [q.name, n(q.quota), <span key="u" className={q.used >= q.quota ? "text-red-600" : "text-[#3566D6]"}>{n(q.used)}</span>,
    <span key="a" className="text-[#1F9D55]">{n(Math.max(0, q.quota - q.used))}</span>]);
  const qTot = quotas.reduce((a, q) => [a[0] + q.quota, a[1] + q.used], [0, 0]);

  return (
    <div className="space-y-4" data-testid="saree-dashboard">
      <DashHero image={url} testid="saree-hero"
        left={[
          { icon: SareeIcon, label: "Today's Receiving", value: n(stats.todays_tokens), testid: "sstat-today" },
          { icon: Inbox, label: "Pending In (Today)", value: n(stats.issued), testid: "sstat-issued" },
          { icon: Warehouse, label: "Godown In Today", value: n(stats.godown_in), testid: "sstat-gin" },
          { icon: PackageOpen, label: "Godown Out Today", value: n(stats.godown_out), testid: "sstat-gout" },
        ]}
        right={[
          { icon: ShoppingBag, label: "Sold Today", value: n(stats.sold), testid: "sstat-sold" },
          { icon: IndianRupee, label: "Sales Today", value: rupees(stats.sales_today), testid: "sstat-sales-today" },
          { icon: Layers, label: "Quantity Received Today", value: n(stats.qty_today), testid: "sstat-qty" },
          { icon: Tag, label: "Saree Types Today", value: n((type_wise || []).length), testid: "sstat-types" },
        ]} />

      <DashCaption temple={temple} title="Sarees Dashboard" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Section title="Stock Overview (Today)">
          <TileGrid cols={3}>
            <Tile icon={Inbox} label="Pending In" value={n(stats.issued)} tone="yellow" />
            <Tile icon={Warehouse} label="Godown In" value={n(stats.godown_in)} tone="blue" />
            <Tile icon={PackageOpen} label="Godown Out" value={n(stats.godown_out)} tone="purple" />
          </TileGrid>
        </Section>
        <Section title="Sales (Today)">
          <TileGrid cols={3}>
            <Tile icon={ShoppingBag} label="Sold" value={n(stats.sold)} tone="green" />
            <Tile icon={IndianRupee} label="Amount" value={rupees(stats.sales_today)} tone="yellow" />
            <Tile icon={Layers} label="Qty In" value={n(stats.qty_today)} tone="red" />
          </TileGrid>
        </Section>
        <Section title="Receiving (Today)">
          <TileGrid cols={3}>
            <Tile icon={SareeIcon} label="Tokens" value={n(stats.todays_tokens)} tone="blue" />
            <Tile icon={Layers} label="Quantity" value={n(stats.qty_today)} tone="green" />
            <Tile icon={Tag} label="Types" value={n((type_wise || []).length)} tone="orange" />
          </TileGrid>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cats.length > 0 && (
          <Section title="Category-wise Sarees (Today)">
            <TileGrid cols={cats.length >= 5 ? 5 : cats.length === 4 ? 4 : cats.length === 3 ? 3 : cats.length}>
              {cats.map((c, i) => <Tile key={c.name} icon={SareeIcon} label={c.name} value={n(c.value)} note={`${n(c.qty)} qty`} tone={TONE_CYCLE[i % TONE_CYCLE.length]} />)}
            </TileGrid>
          </Section>
        )}
        {quotas.length > 0 && (
          <Section title="Monthly Quota Usage" testid="saree-quotas">
            <DashTable head={["Saree Type", "Quota", "Used", "Available"]} rows={quotaRows}
              foot={["TOTAL", n(qTot[0]), n(qTot[1]), n(Math.max(0, qTot[0] - qTot[1]))]} />
          </Section>
        )}
        <Section title="Saree Type-wise Quantity (Today)" bodyClass="p-3 h-64" className={cats.length > 0 && quotas.length > 0 ? "lg:col-span-2" : ""}>
          <ResponsiveContainer>
            <BarChart data={type_wise} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D2" vertical={false} />
              <XAxis dataKey="name" tick={AX} tickLine={false} />
              <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#FDF2E9" }} />
              <Bar dataKey="qty" name="Quantity" fill="#D7261E" radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </div>
    </div>
  );
}
